import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { KeyRound, LogOut, Menu } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const initials = (name) => (name || "").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

function SidebarContent({ portalLabel, tenantName, tenantSubtitle, navSections, user, onLogout, onChangePassword, onNavClick, itemPadding = "py-2" }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-white/10">
        <div className="flex items-center gap-2 text-white font-extrabold text-[15px] tracking-tight">
          <span className="w-6 h-6 rounded-md bg-[#E8792E] flex items-center justify-center text-white text-[10px] font-extrabold">SS</span>
          ScapeSource
        </div>
        <div className="text-[10.5px] uppercase tracking-wider text-[#7EA4C4] font-semibold mt-1">{portalLabel}</div>
      </div>
      {tenantName && (
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center text-[#A9C2D8] text-[10px] font-bold flex-shrink-0">
            {initials(tenantName)}
          </div>
          <div className="min-w-0">
            <div className="text-[12.5px] font-semibold text-white truncate">{tenantName}</div>
            <div className="text-[11px] text-[#7EA4C4] truncate">{tenantSubtitle}</div>
          </div>
        </div>
      )}
      <nav className="flex-1 overflow-y-auto ss-scrollbar py-3 px-3">
        {navSections.map((section, si) => (
          <div key={section.title || si} className="mb-4">
            {section.title && (
              <div className="px-2.5 mb-1.5 text-[10px] uppercase tracking-wider font-semibold text-[#5C7E9C]">{section.title}</div>
            )}
            {section.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                onClick={onNavClick}
                data-testid={`nav-${item.testId}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-2.5 ${itemPadding} rounded-lg text-[13px] font-medium mb-0.5 transition-colors ${
                    isActive ? "ss-sidebar-active text-white" : "text-[#A9C2D8] hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <item.icon size={15} />
                <span className="truncate">{item.label}</span>
                {!!item.badge && (
                  <span className="ml-auto text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5 flex-shrink-0">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="px-4 py-3 border-t border-white/10 flex items-center gap-2.5" data-testid="sidebar-user-info">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
          style={{ background: "linear-gradient(135deg,#38bdf8,#0284c7)" }}
        >
          {initials(user?.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold text-white truncate">{user?.name}</div>
          <div className="text-[11px] text-[#7EA4C4] truncate capitalize">{(user?.role || "").replace(/_/g, " ")}</div>
        </div>
        <button onClick={onChangePassword} data-testid="change-password-button" title="Change password" className="w-9 h-9 flex items-center justify-center text-[#7EA4C4] hover:text-white transition-colors">
          <KeyRound size={15} />
        </button>
        <button onClick={onLogout} data-testid="logout-button" title="Sign out" className="w-9 h-9 flex items-center justify-center text-[#7EA4C4] hover:text-white transition-colors">
          <LogOut size={15} />
        </button>
      </div>
    </div>
  );
}

export default function PortalLayout({ portalLabel, tenantName, tenantSubtitle, navSections, children, showNotifications = true, showSearch = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const handleLogout = async () => {
    const wasStaff = user?.portal === "staff";
    await logout();
    navigate(wasStaff ? "/admin" : "/login");
  };

  const openChangePassword = () => { setDrawerOpen(false); setChangingPassword(true); };
  const sidebarProps = { portalLabel, tenantName, tenantSubtitle, navSections, user, onLogout: handleLogout, onChangePassword: openChangePassword };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900">
      {/* Desktop sidebar — unchanged from the pre-mobile layout, just gated to md+. */}
      <aside className="hidden md:flex ss-sidebar w-[220px] flex-shrink-0 flex-col" data-testid="portal-sidebar">
        <SidebarContent {...sidebarProps} />
      </aside>

      {/* Mobile nav drawer — same sidebar content, slid over the content below md. */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-[260px] max-w-[80vw] p-0 border-0 bg-[#12304A] md:hidden" data-testid="mobile-nav-drawer">
          <SidebarContent {...sidebarProps} onNavClick={() => setDrawerOpen(false)} itemPadding="py-3" />
        </SheetContent>
      </Sheet>

      <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
        {/* Desktop top strip — unchanged. */}
        <div className="hidden md:flex h-14 border-b border-slate-100 items-center justify-end gap-1 px-6">
          {showSearch && <GlobalSearch />}
          {showNotifications && <NotificationBell />}
        </div>
        {/* Mobile sticky top bar — hamburger + portal name + search + bell. */}
        <div className="md:hidden sticky top-0 z-30 h-14 bg-white border-b border-slate-100 flex items-center gap-2 px-3">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-nav-menu-button"
            aria-label="Open navigation menu"
            className="w-11 h-11 -ml-1 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-colors"
          >
            <Menu size={22} />
          </button>
          <div className="flex-1 min-w-0 text-[13.5px] font-bold text-slate-800 truncate">{portalLabel}</div>
          {showSearch && <GlobalSearch />}
          {showNotifications && <NotificationBell />}
        </div>
        {children}
      </main>
      <ChangePasswordDialog open={changingPassword} onOpenChange={setChangingPassword} />
    </div>
  );
}
